import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/motion";
import PhoneMockup from "../components/PhoneMockup";

const SECTIONS = [
  { title: "Why bedtime can feel hard", body: "When the lights go out, everything gets quieter — and little imaginations get louder. Strange shadows, unfamiliar sounds, worries from the day, and missing Mom or Dad can all show up at once. You're not doing anything wrong. Bedtime is genuinely a big moment for small people.", bg: "#FBE7EF" },
  { title: "Creating a familiar routine", body: "Kids relax into what they can predict. A simple, repeatable sequence — bath, pajamas, teeth, story, Buddy, lights-down — gives them something steady to hold onto. The routine itself becomes the comfort.", bg: "#EEE8FF" },
  { title: "How the Buddy fits in", body: "A Bedtime Buddy becomes the part of the routine that stays after you leave. Something soft to hug, a familiar voice telling a story, and a gentle glow beside them — the same every night, in their bed, on trips, at sleepovers.", bg: "#DFF3FF" },
  { title: "Safety, plainly", body: "Bedtime Buddies are comfort companions, not baby products. They're not intended for infant sleep environments, and every Buddy ships with clear age and use guidelines. Full details live on our Safety page.", bg: "#FFF3D1" },
  { title: "Cleaning and care", body: "Life with kids is sticky. The plush outer is designed for surface cleaning, and the electronics module is protected inside. Full care instructions ship with every Buddy.", bg: "#E4F6E9" },
];

export default function ForParents() {
  return (
    <div data-testid="for-parents-page" className="bg-cream">
      <section className="bg-blush px-6 py-20 text-center lg:py-28">
        <Reveal className="mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pinkdeep">For parents</p>
          <h1 className="mt-5 font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">
            You've got enough on your plate at 8pm.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-navy/60">
            Here's everything you actually want to know — no jargon, no clinical claims, just how a Buddy fits into real family bedtime.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl space-y-5 px-6 py-20">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={0.04 * i}>
            <div className="rounded-[2rem] p-8 sm:p-10" style={{ background: s.bg }} data-testid={`parents-section-${i}`}>
              <p className="font-display text-sm font-semibold text-navy/35">{String(i + 1).padStart(2, "0")}</p>
              <h2 className="mt-2 font-display text-2xl font-semibold text-navy">{s.title}</h2>
              <p className="mt-3 text-base leading-relaxed text-navy/65">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-lavender py-24">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-medium text-navy sm:text-4xl">You're always in control.</h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-navy/60">
              Parents control the technology. Kids experience the magic. From the app you choose the story, the glow color, the brightness, the volume and when it all gently winds down.
            </p>
            <Link to="/how-it-works" data-testid="parents-how-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-display font-semibold text-white transition hover:scale-[1.04]">
              See How It Works <ArrowRight size={17} />
            </Link>
          </Reveal>
          <PhoneMockup />
        </div>
      </section>

      <section className="px-6 py-20 text-center">
        <Reveal>
          <h2 className="font-display text-3xl font-medium text-navy">Bedtime, but friendlier.</h2>
          <Link to="/buddy/luna-the-unicorn" data-testid="parents-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink px-9 py-4 font-display font-semibold tracking-wide text-navy transition hover:scale-[1.04] hover:bg-moon">
            Make Bedtime Feel Different <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
